import React, { ReactElement, useContext } from "react";
import { Dialog, Grid, IconButton, useTheme } from "@mui/material";
import Typography from "@mui/material/Typography";
import { GlobalContext } from "../Utils/Contexts";
import CloseIcon from "@mui/icons-material/CancelRounded";

type Release = {
    versionNumber: string;
    releaseDate: string;
};

type Bullet = {
    versionNumber: string;
    note: string;
};

export function ReleaseNotes(): ReactElement {
    const theme = useTheme();
    const context = useContext(GlobalContext);

    const releases: Release[] = [
        { versionNumber: "V1.2.30", releaseDate: "2026/10/06" },
        { versionNumber: "V1.2.29", releaseDate: "2026/09/14" },
        { versionNumber: "V1.2.28", releaseDate: "2026/07/22" },
        { versionNumber: "V1.2.27", releaseDate: "2026/07/20" },
        { versionNumber: "V1.2.26", releaseDate: "2026/02/25" },
        { versionNumber: "V1.2.25", releaseDate: "2026/02/24" },
        { versionNumber: "V1.2.24", releaseDate: "2026/02/04" },
        { versionNumber: "V1.2.23", releaseDate: "2025/09/22" },
        { versionNumber: "V1.2.22", releaseDate: "2025/09/01" },
        { versionNumber: "V1.2.21", releaseDate: "2025/08/26" },
        { versionNumber: "V1.2.20", releaseDate: "2025/01/20" },
    ];

    const releaseBullets: Bullet[] = [
        {
            versionNumber: "V1.2.30",
            note: "The iPad now keeps tickets for the current month and the previous 18 months. Older tickets are removed from the iPad only and remain in RPS.",
        },
        {
            versionNumber: "V1.2.29",
            note: "Allow revising tickets that are up to 45 days old.",
        },
        {
            versionNumber: "V1.2.28",
            note: "Added checks to prevent entering more text than the RPS system can handle (Cost Codes are now limited to 32 characters for example).",
        },
        {
            versionNumber: "V1.2.27",
            note: "Fixed a bug preventing the EMAIL STALLED TICKET DATA menu option from showing in certain cases.",
        },
        {
            versionNumber: "V1.2.26",
            note: 'Removed the clear [X] from the Charge Type and Fuel Source dropdowns, to prevent "ticket partially synced" errors.',
        },
        {
            versionNumber: "V1.2.26",
            note: 'Cleaned up invalid fuel and charge rows and removed the "not a valid document" banner from affected tickets.',
        },
        {
            versionNumber: "V1.2.25",
            note: "Fixed a sync issue that could duplicate charge (or landing or fuel) rows after a failed push.",
        },
        {
            versionNumber: "V1.2.25",
            note: "Only push the latest revision of a ticket, if a partially synced ticket is revised the original will now show as complete.",
        },
        {
            versionNumber: "V1.2.24",
            note: "Tickets flagged with 'DATA INCOMPLETE - REFER TO ORIGINAL EMAILED PDF' can now be viewed/emailed in the app, with a disclaimer that data may be missing.",
        },
        {
            versionNumber: "V1.2.24",
            note: "These DATA INCOMPLETE tickets can now be revised regardless of how old they are if you need to produce a corrected PDF for any reason.",
        },
        {
            versionNumber: "V1.2.24",
            note: "Fixed the underlying issue that caused these DATA INCOMPLETE tickets. If you experience this issue again after updating to version 1.2.24 please use the 'EMAIL STALLED TICKET DATA' menu option to report the issue and send the data in to support.",
        },
        {
            versionNumber: "V1.2.23",
            note: "Added a fuel rate of 280 litres per hour for the EC135 aircraft type.",
        },
        {
            versionNumber: "V1.2.22",
            note: "Fixed an issue with the EMAIL STALLED TICKET DATA option, it will now send the correct data.",
        },
        {
            versionNumber: "V1.2.21",
            note: "Fixed the overlapping border on time fields.",
        },
        {
            versionNumber: "V1.2.21",
            note: "Added EMAIL STALLED TICKET DATA menu option (only visible if you have tickets that will not complete the sync process). When this option is selected, the ticket data will be copied to the iPad clipboard for pasting into an email",
        },
        {
            versionNumber: "V1.2.20",
            note: 'Added this popup for better communication of change. It will be shown automatically after each release and is available from the dropdown menu as "RELEASE NOTES".',
        },
        {
            versionNumber: "V1.2.20",
            note: "Added a user preference option for time entry style with two options, the existing spinners and a new text option.",
        },
        {
            versionNumber: "V1.2.20",
            note: "Times are entered as 4 digits when using the new text style (eg 0830, 1645). This input style only supports 24 hour time.",
        },
        {
            versionNumber: "V1.2.20",
            note: "Removed automatic sorting on tickets with up/down times per leg. A sort button is available when the legs are out of order. This change only affects the entry form, the PDF legs will always be sorted by up time.",
        },
        { versionNumber: "V1.2.20", note: 'Fixed a bug that caused "times cannot overlap" errors to show on flight legs when there was no overlap.' },
        { versionNumber: "V1.2.20", note: 'Number fields will now default to using the "123" keyboard.' },
    ];

    const details: JSX.Element[] = [];

    let oddRow = false;

    releases.forEach((release) => {
        details.push(
            <Grid item key={release.versionNumber} xs={12}>
                <Typography sx={{ fontSize: "1.2em", fontWeight: "bold", pt: 2, ml: 2, mr: 2 }}>{release.versionNumber}</Typography>
            </Grid>
        );
        releaseBullets
            .filter((b) => b.versionNumber === release.versionNumber)
            .forEach((b, index) => {
                details.push(
                    <Grid item key={`${b.versionNumber}-${index}`} xs={12} sx={{ ml: 2, mr: 2, mb: 1 }}>
                        <Typography sx={{ fontSize: "1.1em", backgroundColor: oddRow ? "#F0F0F0" : "#FFF" }}>{b.note}</Typography>
                    </Grid>
                );
                oddRow = !oddRow;
            });
    });

    return (
        <Dialog open={true} fullWidth onClose={handleClose}>
            <Grid container flexDirection={"column"} sx={{ p: 2 }} maxHeight={"70vh"}>
                <Grid container item flexDirection={"column"}>
                    <Grid container item xs={12} alignItems={"center"}>
                        <Grid item xs={11} container justifyContent={"center"}>
                            <Typography component="h1" variant="h6">
                                RELEASE NOTES
                            </Typography>
                        </Grid>
                        <Grid item xs={1} container>
                            <IconButton onClick={handleClose} size={"large"} edge={"end"} sx={{ color: theme.palette.primary.main, marginRight: 1 }}>
                                <CloseIcon fontSize={"large"} />
                            </IconButton>
                        </Grid>
                    </Grid>
                    <Grid container item xs={12} sx={{ pt: 2 }} flexDirection={"row"}>
                        {details}
                    </Grid>
                    <Grid container item xs={12} flex={1} minHeight={2}></Grid>
                </Grid>
            </Grid>
        </Dialog>
    );

    function handleClose() {
        context.showReleaseNotes(false);
    }
}
